import { BookingFormValidator } from "../events/booking-form-validator";
export interface PaymentOptions {
    formId: string;
    keyId?: string;
    contactId?: string;
    successUrl: string;
    failureUrl: string;
    total: string | number;
    item: string;
}

export class PaymentService {
    // private stripe: any;
    // private elements: any;
    // private options: PaymentOptions | null = null;

    /**
     * Core invoice submission shared by event and course paths.
     * Callers supply explicit endpoints rather than a boolean flag.
     */
    private async submitInvoiceCore(
        formId: string,
        keyId: string,
        saveEndpoint: string | undefined,
        crmUrl: string,
        successUrl: string,
        failureUrl: string,
        poNumber: string
    ): Promise<void> {
        BookingFormValidator.setupTermsValidation(formId);

        if (!BookingFormValidator.validateBookingForm(formId)) return;

        const saved = await BookingFormValidator.saveFormToSession(
            formId,
            keyId,
            (form: HTMLFormElement) => this.getBookingFormData(form),
            saveEndpoint
        );
        if (!saved) return;

        const url = new URL(crmUrl, window.location.origin);
        url.searchParams.set("key", keyId);
        url.searchParams.set("successUrl", successUrl);
        url.searchParams.set("failureUrl", failureUrl);
        url.searchParams.set("paymentMethod", "invoice");
        url.searchParams.set("poNumber", poNumber);
        window.location.href = url.toString();
    }

    /**
     * Validates and clears the PO Number input.
     * Returns the trimmed value on success, or null if validation failed.
     */
    private validatePoNumber(): string | null {
        const poInput = document.getElementById("PoNumber") as HTMLInputElement | null;
        const poError = document.getElementById("PoNumberError") as HTMLElement | null;
        const poNumber = (poInput?.value ?? "").trim();

        if (!poNumber) {
            if (poInput) { poInput.classList.add("input-validation-error"); poInput.focus(); }
            if (poError) { poError.textContent = "Please enter a PO number."; poError.classList.remove("d-none"); }
            return null;
        }

        if (poInput) poInput.classList.remove("input-validation-error");
        if (poError) { poError.textContent = ""; poError.classList.add("d-none"); }
        return poNumber;
    }

    public async submitEventInvoiceFromButton(btn: HTMLElement): Promise<void> {
        const poNumber = this.validatePoNumber();
        if (!poNumber) return;

        await this.submitInvoiceCore(
            "EventBookingForm",
            btn.getAttribute("data-event-id") ?? "",
            undefined,
            "/umbraco/surface/EventBookingSurface/SaveEventBookingToCrm",
            btn.getAttribute("data-success-url") ?? "",
            btn.getAttribute("data-failure-url") ?? "",
            poNumber
        );
    }

    // private getFormData(form: HTMLFormElement): { [key: string]: string } {
    //     const formData = new FormData(form);
    //     const indexedArray: { [key: string]: string } = {};

    //     formData.forEach((value, key) => {
    //         indexedArray[key] = value.toString();
    //     });

    //     return indexedArray;
    // }

    private getBookingFormData(form: HTMLFormElement): Record<string, any> {
        const fd = new FormData(form);
        const data: Record<string, any> = {};

        // 1) pull raw values (strings)
        for (const [key, value] of fd.entries()) {
            // ignore files here
            if (value instanceof File) continue;

            // If you have multi-select/checkbox groups with same name, support arrays:
            if (key in data) {
                data[key] = Array.isArray(data[key]) ? [...data[key], value] : [data[key], value];
            } else {
                data[key] = value;
            }
        }

        // 2) helpers
        const toBool = (v: any) => {
            if (typeof v === "boolean") return v;
            if (typeof v === "string") return v.toLowerCase() === "true" || v === "on" || v === "1";
            if (typeof v === "number") return v === 1;
            return false;
        };

        const toNumber = (v: any) => {
            if (typeof v === "number") return v;
            if (typeof v !== "string") return v;
            const n = Number(v);
            return Number.isFinite(n) ? n : v; // if not numeric, leave as-is
        };

        // 3) coerce known numeric fields (add/remove as needed)
        const numberFields = [
            "Total",
            "SubTotal",
            "TotalVat",
            "EarlyBirdDiscountTotal",
            "DiscountTotal",
            "TotalBookings",
            "PaymentMethod"
        ];

        for (const f of numberFields) {
            if (data[f] !== undefined && data[f] !== null && data[f] !== "") {
                data[f] = toNumber(data[f]);
            }
        }

        // 4) coerce known boolean fields (add/remove as needed)
        const boolFields = [
            "SameAsDelivery",
            "InvoiceTermsAgreed",
            "PayByCard",
            "PayByInvoice",
            "ChargedToOrganisationAvailable",
            "ChargeToOrganisation",
            "TermsAllAgreed"
        ];

        for (const f of boolFields) {
            if (data[f] !== undefined && data[f] !== null && data[f] !== "") {
                data[f] = toBool(data[f]);
            }
        }

        return data;
    }

}

